import * as z from "zod";

import type { Contract, Target } from "../types";

// Ideally this schema would just be SalesOrder<true>, but it is currently a
// standard TypeScript interface rather than a Zod type. The catchalls preserve
// unspecified standard and custom fields so mapping functions can still use them.
const netSuiteResourceSchema = z
  .object({ id: z.string().nullish(), refName: z.string().nullish() })
  .catchall(z.unknown());

// NetSuite can omit product fields on discount and other non-product lines.
const netSuiteSalesOrderLineSchema = z
  .object({
    id: z.string().nullish(),
    item: netSuiteResourceSchema.nullish(),
    amount: z.number().nullish(),
    line: z.number().nullish(),
    lineUniqueKey: z.string().nullish(),
    quantity: z.number().nullish(),
    rate: z.number().nullish(),
    taxAmount: z.number().nullish(),
    taxRate1: z.number().nullish(),
  })
  .catchall(z.unknown());

export const netSuiteSalesOrderSchema = z
  .object({
    id: z.string(),
    tranId: z.string(),
    taxTotal: z.number().nullish(),
    shippingCost: z.number().nullish(),
    item: z
      .object({ items: z.array(netSuiteSalesOrderLineSchema) })
      .catchall(z.unknown()),
  })
  .catchall(z.unknown());

export const netSuiteOrderMappingInputSchema = z.object({
  rawOrder: netSuiteSalesOrderSchema,
});
export type NetSuiteOrderMappingInput = z.infer<
  typeof netSuiteOrderMappingInputSchema
>;

export const netSuiteOrderMappingOutputSchema = z.object({
  rawOrder: netSuiteSalesOrderSchema,
});
export type NetSuiteOrderMappingOutput = z.infer<
  typeof netSuiteOrderMappingOutputSchema
>;

export const netSuiteOrderMappingTarget = {
  id: "integrations.netsuite.order-mapping",
  displayName: "NetSuite order mapping",
  description:
    "Updates the merchant's raw NetSuite sales order before Redo converts it into the standard order format. " +
    "It receives and returns the complete raw NetSuite order, then Redo runs its normal order transformation and validation.",
} as const satisfies Target;

export const netSuiteOrderMappingStarterCode = `// Edit this function to update the raw NetSuite sales order before Redo converts it.
export default async (input: Input): Promise<Output> => {
  return { rawOrder: input.rawOrder };
};
`;

export const netSuiteOrderMappingContractV1: Contract = {
  id: `${netSuiteOrderMappingTarget.id}@2026-08.2`,
  targetId: netSuiteOrderMappingTarget.id,
  inputSchema: netSuiteOrderMappingInputSchema,
  outputSchema: netSuiteOrderMappingOutputSchema,
  tools: [],
  sampleInput: {
    rawOrder: {
      id: "12345",
      tranId: "SO-1001",
      taxTotal: 2.4,
      shippingCost: 5,
      item: {
        items: [
          {
            id: "1",
            item: { id: "100", refName: "Example item" },
            amount: 30,
            line: 1,
            lineUniqueKey: "line-1",
            quantity: 1,
            rate: 30,
            custcol_example_tax_rate: 8,
          },
        ],
      },
    },
  } satisfies NetSuiteOrderMappingInput,
};
